'use client';

import { faArrowRight, faPhone } from '@fortawesome/pro-light-svg-icons';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { z } from 'zod';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { updatePhone } from '@/app/(main)/(pages)/(auth)/agendamentos/_components/card-appointment/variants/_services/update-phone.service';
import { Button } from '@/components/ui/button/button.component';
import { FormField } from '@/components/ui/form.component';
import { Input } from '@/components/ui/input/input.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { useToast } from '@/components/ui/use-toast';
import { useLoading } from '@/hooks/use-loading.hook';
import { maskPhone } from '@/utils/formatters/phonenumber';

const inputPhoneSchema = z.object({
  phone: z.string().min(15),
});

type InputPhoneSchemaType = z.infer<typeof inputPhoneSchema>;
interface PhoneUpdateInputProps {
  defaultValue: string;
  onPhoneUpdate?: (phone: string) => void;
}

export function PhoneUpdateInput({ defaultValue, onPhoneUpdate }: PhoneUpdateInputProps) {
  const { toast } = useToast();
  const { startLoading, stopLoading, isLoading } = useLoading();

  const [isEditing, setIsEditing] = useState(false);

  const { control, handleSubmit } = useForm<InputPhoneSchemaType>({
    values: { phone: maskPhone(defaultValue) },
  });

  if (!isEditing) {
    return (
      <Button
        startIcon={faPhone}
        data-testid={DataTestIdEnum.APPOINTMENT_CARD_PHONE_UPDATE_BUTTON}
        onClick={() => setIsEditing(true)}
      >
        Atualizar número
      </Button>
    );
  }

  return (
    <form
      className="flex items-end gap-2 w-full"
      onSubmit={handleSubmit(async (val) => {
        const res = inputPhoneSchema.safeParse(val);
        if (res.success === false) {
          toast.error('Por favor, informe um número de telefone válido');
          return;
        }

        try {
          startLoading();
          await updatePhone({
            phone: val.phone,
          });
          setIsEditing(false);
          onPhoneUpdate?.(val.phone);
          toast.success('Telefone atualizado com sucesso');
        } catch {
          toast.error('Erro ao atualizar telefone');
        } finally {
          stopLoading();
        }
      })}
    >
      <FormField
        control={control}
        name="phone"
        render={({ field }) => (
          <label className="flex flex-1 flex-col gap-1">
            <Typography as="span" variant={TypographyVariant.MEDIUM_BODY_TEXT} className="font-semibold">
              Seu telefone para contato
            </Typography>
            <Input placeholder="(41) 99999-9999" type="tel" mask="(99) 99999-9999" {...field} />
          </label>
        )}
      />
      <Button
        data-testid={DataTestIdEnum.APPOINTMENT_CARD_PHONE_UPDATE_SUBMIT}
        asIcon
        startIcon={faArrowRight}
        isLoading={isLoading}
        type="submit"
        className="w-12 h-12 shadow-button shrink-0"
      />
    </form>
  );
}
